import type { Directive } from 'vue';
import { fetchFile } from '@/api/client';
import { router } from '@/router';

/**
 * Turns the mention links in a rendered body into chips with a face.
 *
 * The server writes `@Ivan Petrov` as an ordinary markdown link to the member's
 * profile — that is all it can do, since the renderer escapes HTML and a link
 * is the only anchor markdown produces. This directive is the other half:
 * it styles those links and puts the person's avatar in front of the name.
 *
 * Avatars cannot be a plain `src`. `GET /files/:id/content` is guarded by the
 * `Authorization` header, so a URL in the HTML would 401 and paint a broken
 * image; the blob is fetched through the authenticated client instead, exactly
 * as `v-prose` does for the images inside a body. Fetches are memoised across
 * every message on screen, because one person mentioned twenty times is one
 * avatar, not twenty downloads.
 *
 * Degrades quietly at every step: no binding, no avatar, or a failed fetch all
 * leave a readable "@Ivan Petrov" behind.
 *
 * ```html
 * <div class="pk-prose" v-mentions="message.mentions" v-prose v-html="message.bodyHtml" />
 * ```
 */
export interface MentionedPerson {
  id: string;
  displayName: string;
  avatarFileId: string | null;
}

/** `/company/members/<uuid>`, with or without an API prefix in front. */
const MEMBER_LINK = /\/company\/members\/([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})$/i;

/**
 * One in-flight or settled fetch per avatar, for the lifetime of the page.
 *
 * Object URLs are deliberately never revoked here. They are a few dozen bytes
 * of bookkeeping each, they are shared by every message that names the same
 * person, and revoking one while another message still points at it is how the
 * second avatar turns into a broken image.
 */
const avatars = new Map<string, Promise<string | null>>();

function avatarUrl(fileId: string): Promise<string | null> {
  let pending = avatars.get(fileId);
  if (!pending) {
    pending = fetchFile(fileId, { thumbnail: true, width: 48 })
      .then((blob) => URL.createObjectURL(blob))
      .catch(() => null);
    avatars.set(fileId, pending);
  }
  return pending;
}

function personIdOf(anchor: HTMLAnchorElement): string | null {
  const href = anchor.getAttribute('href') ?? '';
  try {
    return MEMBER_LINK.exec(new URL(href, window.location.origin).pathname)?.[1] ?? null;
  } catch {
    return null;
  }
}

function decorate(el: HTMLElement, people: MentionedPerson[]): void {
  for (const anchor of el.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    // `data-mention` is the "already done" marker; without it a re-render would
    // stack a second avatar in front of the first.
    if (anchor.dataset.mention) continue;

    const id = personIdOf(anchor);
    if (!id) continue;

    anchor.dataset.mention = id;
    anchor.className = 'pk-mention';

    /**
     * Kept inside the app.
     *
     * `renderMarkdown` gives every link `target="_blank"`, which is right for
     * the outside world and wrong for a colleague's name: it opened a second
     * tab that booted the SPA from scratch, and a fresh boot picks the default
     * company rather than the one the message was written in — so the mention
     * showed the right person under the wrong organisation. Routing in place
     * keeps the workspace that is already loaded.
     */
    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
    anchor.addEventListener('click', (event) => {
      // Let the browser have the clicks that mean "somewhere else": a new tab,
      // a new window, a download.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      void router.push(anchor.getAttribute('href') ?? '/');
    });

    const person = people.find((entry) => entry.id === id);
    if (!person?.avatarFileId) continue;

    const image = document.createElement('img');
    image.className = 'pk-mention-avatar';
    image.alt = '';
    anchor.prepend(image);

    void avatarUrl(person.avatarFileId).then((url) => {
      // The body may have been replaced while this was in flight.
      if (url && image.isConnected) image.src = url;
      else image.remove();
    });
  }
}

export const vMentions: Directive<HTMLElement, MentionedPerson[] | undefined> = {
  mounted(el, binding) {
    decorate(el, binding.value ?? []);
  },
  updated(el, binding) {
    decorate(el, binding.value ?? []);
  },
};
